import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { config } from '../lib/config';
import { acledPosts } from '../lib/acled';
import type { AcledPost } from '../types/acled';
import { BottomSheet } from './BottomSheet';
import { EmptyState, Skeleton } from './States';
import { Button } from './Button';
type Filter = 'All' | 'Combat' | 'Civilians' | 'Protests' | 'Verified';
const filterTypes = (p: AcledPost, filter: Filter) =>
  filter === 'All' ||
  (filter === 'Combat' && p.type === 'Battles') ||
  (filter === 'Civilians' && p.type.includes('Violence against civilians')) ||
  (filter === 'Protests' && p.type.includes('Protest')) ||
  (filter === 'Verified' && (p.src.includes(';') || p.flair.includes('flash')));
const color = (p: AcledPost) =>
  p.type === 'Battles'
    ? 'red'
    : p.type.includes('civilian')
      ? 'orange'
      : p.type.includes('Protest')
        ? 'yellow'
        : 'blue';
const esc = (value: string) =>
  value.replace(
    /[&<>'"]/g,
    (x) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[x]!,
  );
const details = (post: AcledPost, num?: number) => (
  <div className="event-detail">
    <span className={`event-type event-type--${color(post)}`}>{post.type}</span>
    {num ? <span className="event-num">POST #{num}</span> : null}
    <h3>{post.title}</h3>
    <dl>
      <div>
        <dt>Fatalities</dt>
        <dd>{post.fat}</dd>
      </div>
      <div>
        <dt>Date</dt>
        <dd>{post.date}</dd>
      </div>
      <div>
        <dt>Region</dt>
        <dd>
          {post.country} · {post.admin1}
        </dd>
      </div>
      <div>
        <dt>Source</dt>
        <dd>{post.src}</dd>
      </div>
    </dl>
    <p>{post.body}</p>
    <Button>Open report</Button>
  </div>
);
export interface MapTarget { place: string; lat: number; lng: number; date?: string; note?: string }
export function MapView({
  compact = false,
  center = [31.6, 49] as [number, number],
  zoom,
  events,
  targets,
  onMapReady,
}: {
  compact?: boolean;
  center?: [number, number];
  zoom?: number;
  events?: AcledPost[];
  targets?: MapTarget[];
  onMapReady?: (map: mapboxgl.Map) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const filterRef = useRef<Filter>('All');
  const [coord, setCoord] = useState('49.00N 31.60E · ZOOM 4.6');
  const [mapStatus, setMapStatus] = useState('starting');
  const [fallback, setFallback] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<Filter>('All');
  const [visible, setVisible] = useState<AcledPost[]>([]);
  const [selected, setSelected] = useState<{ post: AcledPost; num: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [listOpen, setListOpen] = useState(true);
  const posts = useMemo(() => events ?? acledPosts(), [events]);
  const risk = useMemo(
    () =>
      Math.min(
        99,
        Math.round(28 + posts.length * 5 + posts.reduce((sum, p) => sum + p.fat, 0) / 10),
      ),
    [posts],
  );
  const updateVisible = useCallback(
    (map: mapboxgl.Map, current: Filter) => {
      setLoading(true);
      window.setTimeout(() => {
        const b = map.getBounds();
        setVisible(
          posts
            .filter((p) => filterTypes(p, current) && Boolean(b?.contains([p.lng, p.lat])))
            .slice(0, 50),
        );
        setLoading(false);
      }, 180);
    },
    [posts],
  );
  const openEvent = useCallback((post: AcledPost, num: number, map?: mapboxgl.Map) => {
    const active = map ?? mapRef.current;
    if (!active) return;
    popupRef.current?.remove();
    if (window.matchMedia('(max-width: 767px)').matches) {
      setSelected({ post, num });
      return;
    }
    const node = document.createElement('div');
    node.className = 'map-popup';
    node.innerHTML = `<span class="event-type event-type--${color(post)}">${esc(post.type)}</span><span class="event-num">POST #${num}</span><h3>${esc(post.title)}</h3><dl><div><dt>Fatalities</dt><dd>${post.fat}</dd></div><div><dt>Date</dt><dd>${esc(post.date)}</dd></div><div><dt>Region</dt><dd>${esc(post.country)} · ${esc(post.admin1)}</dd></div><div><dt>Source</dt><dd>${esc(post.src)}</dd></div></dl><button type="button">Open report</button>`;
    node.querySelector('button')?.addEventListener('click', () => setSelected({ post, num }));
    popupRef.current = new mapboxgl.Popup({ offset: 14, closeButton: true, maxWidth: '320px' })
      .setLngLat([post.lng, post.lat])
      .setDOMContent(node)
      .addTo(active);
  }, []);
  useEffect(() => {
    const container = host.current;
    if (!container) return;
    if (!config.mapboxToken) {
      setFallback('Map service is not configured. Add VITE_MAPBOX_TOKEN to continue.');
      setLoading(false);
      return;
    }
    if (!mapboxgl.supported()) {
      setFallback('WebGL is unavailable on this device.');
      setLoading(false);
      return;
    }
    mapboxgl.accessToken = config.mapboxToken;
    let disposed = false;
    let map: mapboxgl.Map;
    try {
      map = new mapboxgl.Map({
        container,
        style: 'mapbox://styles/mapbox/dark-v11',
        center,
        zoom: zoom ?? (compact ? 3.2 : 4.6),
        attributionControl: false,
        failIfMajorPerformanceCaveat: false,
      });
      mapRef.current = map;
      (container as any).__zenithMap = map;
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');
      if (!compact && (config.e2e || import.meta.env.DEV)) (window as any).__zenithMap = map;
      if (onMapReady) onMapReady(map);
      setMapStatus('loading');
      map.on('error', (event) => {
        if (disposed) return;
        if (!map.loaded() && event.error) {
          setFallback('M4 · Map service blocked or unavailable: ' + (event.error?.message || 'network') + ' — ad-blocker/VPN can block api.mapbox.com.');
          setMapStatus('error');
        }
      });
      map.once('style.load', () => setMapStatus('style ok · tiles loading'));
    } catch {
      setFallback('The operational map could not initialize.');
      setLoading(false);
      return;
    }
    const resize = new ResizeObserver(() => {
      if (!disposed) map.resize();
    });
    resize.observe(container);
    map.once('load', () => {
      if (disposed) return;
      const features = posts.map((post, index) => ({
        type: 'Feature' as const,
        properties: {
          index,
          num: index + 1,
          type: post.type,
          fatalities: post.fat,
          title: post.title,
          verified: post.src.includes(';') || post.flair.includes('flash'),
        },
        geometry: { type: 'Point' as const, coordinates: [post.lng, post.lat] },
      }));
      map.addSource('events', { type: 'geojson', data: { type: 'FeatureCollection', features } });
      map.addLayer({
        id: 'risk-layer',
        type: 'heatmap',
        source: 'events',
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'fatalities'], 0, 0, 50, 1],
          'heatmap-intensity': 1.1,
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0,
            'rgba(74,125,255,0)',
            0.4,
            'rgba(74,125,255,.55)',
            0.72,
            'rgba(242,153,74,.72)',
            1,
            'rgba(229,72,77,.9)',
          ],
          'heatmap-radius': compact ? 18 : 32,
        },
      });
      map.addLayer({
        id: 'acled-events',
        type: 'circle',
        source: 'events',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'fatalities'], 0, 5, 50, 13],
          'circle-color': [
            'match',
            ['get', 'type'],
            'Battles',
            '#e5484d',
            'Violence against civilians',
            '#f2994a',
            'Protests',
            '#f2c94c',
            '#4a7dff',
          ],
          'circle-opacity': 0.94,
          'circle-stroke-color': '#05070a',
          'circle-stroke-width': 2,
        },
      });
      map.addLayer({
        id: 'acled-labels',
        type: 'symbol',
        source: 'events',
        layout: {
          'text-field': ['get', 'num'],
          'text-size': 11,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-offset': [0, 0.8],
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': 'rgba(5,7,10,0.95)',
          'text-halo-width': 2.2,
          'text-halo-blur': 0.6,
        },
      });
      if (!compact) {
        const hoverLayer = ['acled-events', 'acled-labels'];
        map.on('mouseenter', hoverLayer as any, (event) => {
          map.getCanvas().style.cursor = 'pointer';
          const feature = event.features?.[0] as unknown as {
            properties?: { index?: number };
          };
          const post = posts[Number(feature?.properties?.index)];
          if (post && !popupRef.current) {
            popupRef.current = new mapboxgl.Popup({
              offset: 12,
              closeButton: false,
              className: 'map-hover-popup',
            })
              .setLngLat([post.lng, post.lat])
              .setText(post.title)
              .addTo(map);
          }
        });
        map.on('mouseleave', hoverLayer as any, () => {
          map.getCanvas().style.cursor = '';
          popupRef.current?.remove();
          popupRef.current = null;
        });
        map.on('click', (event) => {
          const feature = map.queryRenderedFeatures(event.point, {
            layers: ['acled-events', 'acled-labels'],
          })[0] as { properties?: { index?: number } } | undefined;
          const index = Number(feature?.properties?.index);
          const post = posts[index];
          if (post) openEvent(post, index + 1, map);
          else {
            popupRef.current?.remove();
            popupRef.current = null;
            setSelected(null);
          }
        });
      }
      map.resize();
      updateVisible(map, filterRef.current);
      let tiles = 0;
      try {
        const caches = (map.style as any)?._sourceCaches ?? {};
        tiles = Object.values(caches).reduce((n: number, c: any) => n + (c.getTiles?.().length || 0), 0);
      } catch { /* ignore */ }
      setMapStatus(`ok · ${tiles} tiles`);
      (targets ?? []).forEach((t) => {
        const el = document.createElement('div');
        el.className = 'target-marker';
        el.innerHTML = '▲';
        const pop = new mapboxgl.Popup({ offset: 18, maxWidth: '260px' }).setHTML(
          `<span class="target-pop-title">${esc(t.place)}</span>${t.date ? `<span class="target-pop-date">${esc(t.date)}</span>` : ''}${t.note ? `<p>${esc(t.note)}</p>` : ''}`,
        );
        new mapboxgl.Marker({ element: el })
          .setLngLat([t.lng, t.lat])
          .setPopup(pop)
          .addTo(map);
      });
    });
    map.on('move', () => {
      const c = map.getCenter();
      setCoord(
        `${Math.abs(c.lat).toFixed(2)}${c.lat >= 0 ? 'N' : 'S'} ${Math.abs(c.lng).toFixed(2)}${c.lng >= 0 ? 'E' : 'W'} · ZOOM ${map.getZoom().toFixed(1)}`,
      );
    });
    map.on('moveend', () => updateVisible(map, filterRef.current));
    return () => {
      disposed = true;
      resize.disconnect();
      popupRef.current?.remove();
      mapRef.current = null;
      map.remove();
    };
  }, [compact, openEvent, posts, updateVisible, targets, onMapReady, reloadKey]);
  useEffect(() => {
    filterRef.current = filter;
    const map = mapRef.current;
    if (!map?.getLayer('acled-events')) return;
    const expression: mapboxgl.FilterSpecification =
      filter === 'All'
        ? ['all']
        : filter === 'Combat'
          ? ['==', ['get', 'type'], 'Battles']
          : filter === 'Civilians'
            ? ['==', ['get', 'type'], 'Violence against civilians']
            : filter === 'Protests'
              ? ['==', ['get', 'type'], 'Protests']
              : ['==', ['get', 'verified'], true];
    map.setFilter('acled-events', expression);
    map.setFilter('acled-labels', expression);
    map.setFilter('risk-layer', expression);
    updateVisible(map, filter);
  }, [filter, updateVisible]);
  const fly = (post: AcledPost) => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({ center: [post.lng, post.lat], zoom: 8, duration: 700 });
    const num = posts.indexOf(post) + 1;
    map.once('moveend', () => openEvent(post, num, map));
  };
  return (
    <section className={`map-panel${compact ? ' map-panel--compact' : ''}`}>
      <div className="map" data-testid="map">
        <div ref={host} className="map__host" />
        {fallback && (
          <div className="map__fallback" role="status">
            <svg viewBox="0 0 200 120" aria-hidden="true">
              <path d="M10 95 C 40 80 60 55 95 60 C 130 65 150 45 190 50 L 190 110 L 10 110 Z" fill="#141b26" stroke="#2a3648" strokeWidth="1.5" />
              <circle cx="95" cy="60" r="3" fill="#e5484d" />
              <circle cx="130" cy="72" r="2.5" fill="#f2994a" />
              <circle cx="70" cy="78" r="2" fill="#f2c94c" />
            </svg>
            <strong>Map unavailable</strong>
            <span>{fallback}</span>
            <button
              className="map__retry"
              onClick={() => {
                setFallback('');
                setReloadKey((k) => k + 1);
              }}
            >
              ↻ Retry
            </button>
          </div>
        )}
        <div className="map__badge">LIVE EVENT LAYER</div>
        <div className="map__coord">{coord}</div>
        <div className={`map__status map__status--${mapStatus.includes('ok') ? 'ok' : mapStatus === 'error' ? 'err' : 'wip'}`}>
          MAP {mapStatus}
        </div>
        {!compact && (
          <div className="risk">
            <small>Risk score</small>
            <strong>{risk}</strong>
            <span>ORANGE ▲</span>
          </div>
        )}
      </div>
      {!compact && (
        <>
          <div className="map-filters" aria-label="Event filters">
            {(['All', 'Combat', 'Civilians', 'Protests', 'Verified'] as Filter[]).map((item) => (
              <button
                className={filter === item ? 'active' : ''}
                key={item}
                onClick={() => setFilter(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <section className={`map-event-rail${listOpen ? ' is-open' : ''}`}>
            <button
              className="map-event-rail__head"
              onClick={() => setListOpen((x) => !x)}
              aria-expanded={listOpen}
            >
              <i />
              <span>Events in this view</span>
              <b>{visible.length}</b>
            </button>
            {listOpen && (
              <div className="map-event-list">
                {loading ? (
                  <Skeleton rows={3} />
                ) : visible.length === 0 ? (
                  <EmptyState title="No events in this view" message="Zoom out or change filter." />
                ) : (
                  visible.map((post, index) => (
                    <button key={`${post.title}-${index}`} onClick={() => fly(post)}>
                      <i className={`dot dot--${color(post)}`} />
                      <span>
                        <strong>{post.title}</strong>
                        <small>
                          #{posts.indexOf(post) + 1} · {post.type}
                        </small>
                      </span>
                      <b>
                        {post.fat}
                        <small>FAT</small>
                      </b>
                    </button>
                  ))
                )}
              </div>
            )}
          </section>
          <div className="legend">
            <span>
              <i className="dot dot--red" />
              Combat
            </span>
            <span>
              <i className="dot dot--orange" />
              Civilian
            </span>
            <span>
              <i className="dot dot--yellow" />
              Protest
            </span>
            <span>
              <i className="dot dot--blue" />
              Strategic
            </span>
          </div>
        </>
      )}
      {selected && (
        <BottomSheet
          title={`Event #${selected.num}`}
          subtitle={`${selected.post.country} · ${selected.post.admin1}`}
          onClose={() => setSelected(null)}
        >
          {details(selected.post, selected.num)}
        </BottomSheet>
      )}
    </section>
  );
}
